<template>
  <div class="p-4">
    <v-card flat rounded="lg" class="notification-detail-card">
      <v-card-title class="d-flex align-center justify-space-between px-4 py-3">
        <span class="text-h6 font-weight-medium">
          <font-awesome-icon icon="fa-solid fa-arrow-left" class="me-2 back-link" @click="$router.push('/')" />
          {{ notification ? notification.title : 'Notification' }}
        </span>
        <v-chip v-if="notification" size="small" variant="tonal" :color="statusColor(notification.status)">
          {{ notification.status }}
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text v-if="!loaded" class="px-4 py-6 text-center text-medium-emphasis">
        Loading…
      </v-card-text>

      <v-card-text v-else-if="!notification" class="px-4 py-6">
        <v-alert type="error" variant="tonal" density="comfortable" rounded="lg">
          Notification not found.
        </v-alert>
      </v-card-text>

      <v-card-text v-else class="px-4 py-4">
        <div class="text-body-2 mb-4">{{ notification.message }}</div>

        <v-alert
          v-if="details && !details.mathFullyExplainsHoldings"
          type="warning" variant="tonal" density="comfortable" rounded="lg" class="mb-4"
        >
          The detected ratio doesn't cleanly explain at least one held position below (the resulting quantity isn't
          a whole number) -- review the numbers carefully, and edit them if needed, before applying.
        </v-alert>

        <template v-if="details && details.stockHistoryCorrection">
          <div class="text-subtitle-1 font-weight-medium mb-2">Backtest history correction</div>
          <p class="text-body-2 text-medium-emphasis mb-2">
            {{ details.stockHistoryCorrection.affectedRowCount }} historical price row(s) before
            {{ formatDate(details.date) }} would be rescaled by the {{ details.stockHistoryCorrection.ratioLabel }}
            ratio, so the gap-fade backtest stops treating this corporate action as a fake trading gap.
          </p>
          <v-table density="compact" class="mb-4 preview-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Old close</th>
                <th>New close (after Apply)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in details.stockHistoryCorrection.preview" :key="row.date">
                <td>{{ formatDate(row.date) }}</td>
                <td>{{ $helper.formatPrice(row.oldClose) }}</td>
                <td>{{ $helper.formatPrice(row.newClose) }}</td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <template v-if="details && details.holdingsCorrections && details.holdingsCorrections.length">
          <div class="text-subtitle-1 font-weight-medium mb-2">Holdings adjustment</div>
          <v-table density="compact" class="mb-4 holdings-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Qty (old → new)</th>
                <th>Avg price (old → new)</th>
                <th>Investment</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(holding, index) in details.holdingsCorrections" :key="holding.stock_id">
                <td>{{ holding.platformName }}</td>
                <td>
                  {{ holding.oldQty }} →
                  <v-text-field
                    v-model.number="details.holdingsCorrections[index].newQty"
                    type="number" density="compact" hide-details variant="outlined"
                    class="holding-edit-input"
                  />
                </td>
                <td>
                  {{ $helper.formatPrice(holding.oldAvgPrice) }} →
                  <v-text-field
                    v-model.number="details.holdingsCorrections[index].newAvgPrice"
                    type="number" step="0.01" density="compact" hide-details variant="outlined"
                    class="holding-edit-input"
                  />
                </td>
                <td>{{ $helper.formatPrice(holding.newInvestment) }}</td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <v-alert
          v-if="details && !details.stockHistoryCorrection && !(details.holdingsCorrections && details.holdingsCorrections.length)"
          type="info" variant="tonal" density="comfortable" rounded="lg" class="mb-4"
        >
          No backtest history table or currently-held position found for this code -- nothing to correct.
        </v-alert>

        <div v-if="notification.status === 'pending'" class="d-flex ga-3">
          <v-btn color="primary" variant="tonal" rounded="lg" :loading="applying" @click="apply">
            Apply
          </v-btn>
          <v-btn variant="text" rounded="lg" :loading="dismissing" @click="dismiss">
            Cancel
          </v-btn>
        </div>
        <v-alert v-else type="info" variant="tonal" density="comfortable" rounded="lg">
          This notification was {{ notification.status }}{{ notification.resolved_at ? ` on ${formatDate(notification.resolved_at)}` : '' }}.
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import { useAlertStore } from '@/stores';

export default {
  name: 'NotificationDetail-page',
  setup() {
    return { alertStore$: useAlertStore() };
  },
  data() {
    return {
      notification: null,
      details: null,
      loaded: false,
      applying: false,
      dismissing: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loaded = false;
      const id = this.$route.params.id;
      return fetchWrapper.get(`api/notifications/${id}`).then((res) => {
        this.notification = res?.data || null;
        this.details = this.notification
          ? JSON.parse(JSON.stringify(this.notification.details || null))
          : null;
        this.loaded = true;
      }).catch((err) => {
        console.error('Failed to load notification:', err);
        this.notification = null;
        this.loaded = true;
      });
    },

    statusColor(status) {
      if (status === 'pending') return 'warning';
      if (status === 'applied') return 'success';
      return 'default';
    },

    formatDate(value) {
      return value ? this.$helper.formatDate(value) : '—';
    },

    apply() {
      this.applying = true;
      const id = this.$route.params.id;
      fetchWrapper.post(`api/notifications/${id}/apply`, { details: this.details }).then(() => {
        this.alertStore$.success('Correction applied.');
        this.load();
      }).catch((err) => {
        console.error('Failed to apply notification correction:', err);
        this.alertStore$.error(err.message || 'Failed to apply correction.');
      }).finally(() => {
        this.applying = false;
      });
    },

    dismiss() {
      this.dismissing = true;
      const id = this.$route.params.id;
      fetchWrapper.post(`api/notifications/${id}/dismiss`).then(() => {
        this.alertStore$.success('Notification dismissed.');
        this.load();
      }).catch((err) => {
        console.error('Failed to dismiss notification:', err);
        this.alertStore$.error(err.message || 'Failed to dismiss notification.');
      }).finally(() => {
        this.dismissing = false;
      });
    },
  },
};
</script>

<style scoped>
.back-link {
  cursor: pointer;
}
.holding-edit-input {
  display: inline-block;
  max-width: 120px;
  vertical-align: middle;
}
.preview-table th,
.holdings-table th {
  font-weight: 500;
}
</style>
